import { LocalStorage } from '@/utils/LocalStorage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Consent {
  systemId: string;
  ordinal: number;
  downloadLink?: string | null;
  content: string;
  choices: {
    recordValue: string;
    content: string;
    ordinal: number;
    errorMessage?: string | null;
  }[];
  header?: string | null;
  isMinor: boolean;
  isWithChoice: boolean;
}

export enum RegistrationTrack {
  KitId = 'kitId',
  AccountCreation = 'accountCreation',
  PersonalInformation = 'personalInformation',
  ContactInformation = 'contactInformation',
  AddressConfirmation = 'addressConfirmation',
  RegistrationConfirmation = 'registrationConfirmation',
  ConsentForm = 'consentForm',
  TermsOfService = 'termsOfService',
}

export interface PhoneCode {
  countryPhoneCode: string;
  countryCode: string;
}

/** PRO org from API 1; registration-scoped until promoted to context at success. */
export interface ProKitOrg {
  id: number;
  name: string;
}

export interface IRegisterStore {
  kitId: string;
  initialized: boolean;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  country: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  phoneCode: PhoneCode;
  apartment: string;
  addressType: string | undefined;
  shippable: boolean;
  offers: boolean;
  registrationDone: boolean;
  restrictedRegistrationSteps: RegistrationTrack[];
  consents: Consent[];
  /** PRO org for the kit being registered; null for standard kits. */
  proKitOrg: ProKitOrg | null;

  initialize: (state: { kitId: string }) => void;
  setKitId: (kitId: string) => void;
  setPhoneCode: (phoneCode: PhoneCode) => void;
  setShippable: (value: boolean) => void;
  setOffers: (value: boolean) => void;
  setConsents: (consents: Consent[]) => void;
  setProKitOrg: (org: ProKitOrg | null) => void;
  setAccountCreation: (email: string, password: string) => void;
  setPersonalInformation: (
    firstName: string,
    lastName: string,
    dob: string,
    gender: string,
  ) => void;
  setAddressDetails: (details: {
    country: string;
    street: string;
    apartment: string;
    state: string;
    zip: string;
    phone: string;
    phoneCode: PhoneCode;
    city: string;
  }) => void;
  setAddressType: (type: string | undefined) => void;
  setRegistrationDone: (done: boolean) => void;
  setClearRegisterState: () => void;
  setRestrictedRegistrationSteps: (tracks: RegistrationTrack[]) => void;
  setFinalRegistrationCleanUp: (
    done: boolean,
    tracks: RegistrationTrack[],
  ) => void;
}

const useRegisterStore = create<IRegisterStore>()(
  persist(
    (set) => ({
      kitId: '',
      initialized: false,
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      dob: '',
      gender: '',
      country: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      phoneCode: { countryPhoneCode: '', countryCode: '' },
      apartment: '',
      addressType: undefined,
      shippable: false,
      offers: false,
      registrationDone: false,
      restrictedRegistrationSteps: [],
      consents: [],
      proKitOrg: null,

      initialize: (state) =>
        set({
          kitId: state.kitId,
          initialized: true,
        }),
      setKitId: (kitId) => set({ kitId }),
      setPhoneCode: (phoneCode) => set({ phoneCode }),
      setShippable: (shippable) => set({ shippable }),
      setOffers: (offers) => set({ offers }),
      setConsents: (consents) => set({ consents }),
      setProKitOrg: (proKitOrg) => set({ proKitOrg }),

      setAccountCreation: (email, password) => set({ email, password }),

      setPersonalInformation: (firstName, lastName, dob, gender) =>
        set({ firstName, lastName, dob, gender }),

      setAddressDetails: ({
        country,
        street,
        apartment,
        state,
        zip,
        phone,
        phoneCode,
        city,
      }) =>
        set({ country, street, apartment, state, zip, phone, phoneCode, city }),
      setAddressType: (addressType) => set({ addressType }),
      setClearRegisterState: () => {
        set({
          kitId: '',
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          dob: '',
          gender: '',
          country: '',
          street: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          phoneCode: { countryPhoneCode: '', countryCode: '' },
          apartment: '',
          shippable: false,
          offers: false,
          registrationDone: false,
          restrictedRegistrationSteps: [],
          consents: [],
          proKitOrg: null,
        });
        LocalStorage.removeItem('register');
      },
      setRegistrationDone: (registrationDone) => set({ registrationDone }),
      setRestrictedRegistrationSteps: (restrictedRegistrationSteps) =>
        set({ restrictedRegistrationSteps }),
      setFinalRegistrationCleanUp: (
        registrationDone,
        restrictedRegistrationSteps,
      ) => set({ registrationDone, restrictedRegistrationSteps }),
    }),
    {
      name: 'register',
    },
  ),
);

export default useRegisterStore;
